// Pure helpers for agent knowledge documents. Extracted from the view so the
// rules in docs/agent-knowledge-spec.md are testable on their own.
//
// The two rules worth knowing before changing anything here:
//   - Nothing an agent proposes is usable until a human accepts it.
//   - Every document carries a review date, because knowledge that has quietly
//     gone stale is worse than no knowledge: it steers an agent into working
//     around a problem that was fixed months ago, and nothing surfaces it.

export const KINDS = ['charter', 'policy', 'procedure', 'gotcha', 'reference'];
export const STATUSES = ['proposed', 'accepted', 'retired'];

/** How long each kind stays trustworthy before someone must re-confirm it. */
const REVIEW_MONTHS = {
  charter:   12,  // who we are — changes rarely
  policy:    12,
  procedure:  6,
  gotcha:     6,  // the most perishable: the thing it warns about often gets fixed
  reference: 12,
};

export const WORKSPACE_SCOPE = 'workspace';

export function isFunctionScope(scope) {
  return typeof scope === 'string' && scope.startsWith('function:');
}

export function functionIdOfScope(scope) {
  return isFunctionScope(scope) ? scope.slice('function:'.length) : null;
}

/** The scopes a user can file knowledge under: the workspace, plus each team. */
export function availableScopes(nodes = []) {
  const fns = (nodes || [])
    .filter(n => n.type === 'functionNode')
    .map(n => ({ value: `function:${n.id}`, label: n.data?.label || n.id }));
  return [{ value: WORKSPACE_SCOPE, label: 'Whole workspace' }, ...fns];
}

export function scopeLabel(scope, nodes = []) {
  if (scope === WORKSPACE_SCOPE) return 'Whole workspace';
  const id = functionIdOfScope(scope);
  const fn = (nodes || []).find(n => n.id === id);
  // A function can be deleted while its knowledge survives. Say so rather than
  // rendering a raw node id, and keep the document visible so it can be
  // re-filed instead of silently orphaned.
  return fn ? (fn.data?.label || id) : 'Team no longer exists';
}

export function defaultReviewDue(kind, fromISO) {
  const months = REVIEW_MONTHS[kind] ?? 6;
  const d = fromISO ? new Date(fromISO) : new Date();
  if (isNaN(d.getTime())) return null;
  const out = new Date(d);
  out.setMonth(out.getMonth() + months);
  return out.toISOString().slice(0, 10);
}

/**
 * 'overdue' | 'due-soon' | 'ok' | 'none'
 * `todayISO` is passed in so this never depends on the clock.
 */
export function reviewState(doc, todayISO) {
  if (!doc?.reviewDue) return 'none';
  const due = new Date(doc.reviewDue);
  const today = new Date(todayISO);
  if (isNaN(due.getTime()) || isNaN(today.getTime())) return 'none';
  const days = Math.round((due - today) / 86400000);
  if (days < 0) return 'overdue';
  if (days <= 30) return 'due-soon';
  return 'ok';
}

/** Blocking problems only — things that make a document unusable or untraceable. */
export function validateKnowledge(doc) {
  const errors = [];
  const title = (doc?.title || '').trim();
  if (!title) errors.push('Give it a title.');
  // Retrieval matches on titles, so a vague one is effectively unfindable.
  else if (title.length < 8) errors.push('Make the title specific — it is what retrieval matches on.');
  if (!(doc?.body || '').trim()) errors.push('Give it some content.');
  if (!doc?.scope) errors.push('Choose where it applies.');
  if (!KINDS.includes(doc?.kind)) errors.push('Choose a kind.');
  return errors;
}

/** What a reviewer should look at first: proposals, then knowledge going stale. */
export function sortForReview(docs = [], todayISO) {
  const rank = (d) => {
    if (d.status === 'proposed') return 0;
    const rs = reviewState(d, todayISO);
    if (rs === 'overdue') return 1;
    if (rs === 'due-soon') return 2;
    if (d.status === 'retired') return 4;
    return 3;
  };
  return [...docs].sort((a, b) => {
    const r = rank(a) - rank(b);
    if (r !== 0) return r;
    return (b.updatedAt || '').localeCompare(a.updatedAt || '');
  });
}

export function summarise(docs = [], todayISO) {
  return {
    total:    docs.length,
    accepted: docs.filter(d => d.status === 'accepted').length,
    proposed: docs.filter(d => d.status === 'proposed').length,
    retired:  docs.filter(d => d.status === 'retired').length,
    // Only accepted documents are ever read by an agent, so a stale proposal or
    // a stale retired note is not a problem worth flagging.
    stale:    docs.filter(d => d.status === 'accepted' && reviewState(d, todayISO) === 'overdue').length,
  };
}

/** Group for display, workspace first then each team. */
export function groupByScope(docs = [], nodes = []) {
  const groups = new Map();
  for (const d of docs) {
    const key = d.scope || WORKSPACE_SCOPE;
    if (!groups.has(key)) groups.set(key, { scope: key, label: scopeLabel(key, nodes), docs: [] });
    groups.get(key).docs.push(d);
  }
  return [...groups.values()].sort((a, b) => {
    if (a.scope === WORKSPACE_SCOPE) return -1;
    if (b.scope === WORKSPACE_SCOPE) return 1;
    return a.label.localeCompare(b.label);
  });
}
